import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, IconButton, ListItemIcon, Menu, MenuItem, Paper,
    Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Layout from './Layout';
import ConfirmModal from './modal/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import EmployeeService from '../services/EmployeeService';
import AbsenceService from '../services/AbsenceService';
import AbsenceBalanceCard from './sub-components/absences/AbsenceBalanceCard';
import AbsenceTable from './sub-components/absences/AbsenceTable';
import AbsenceRequestDrawer from './sub-components/absences/AbsenceRequestDrawer';
import ConvertToSickDialog from './sub-components/absences/ConvertToSickDialog';
import { AuthContext } from '../contexts/AuthContext';
import Roles from '../config/roles';

const CONTRACT_TYPES = ['FULL_TIME', 'PART_TIME', 'CONTRACTOR', 'INTERN'];
const STATUS_OPTIONS = ['ACTIVE', 'ON_LEAVE', 'TERMINATED'];
const SALARY_TYPES   = ['MONTHLY', 'HOURLY'];
const CURRENCIES     = ['CHF', 'EUR', 'USD'];

const STATUS_COLOR = {
    ACTIVE:     '#2EC4B6',
    ON_LEAVE:   '#F4A261',
    TERMINATED: '#EF4444',
};

const ACCOUNT_COLOR = {
    NONE:    '#94A3B8',
    INVITED: '#F4A261',
    ACTIVE:  '#2EC4B6',
};

const SectionHeader = ({ label, action }) => (
    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{label}</Typography>
        {action}
    </Box>
);

const FieldRow = ({ label, value }) => (
    <Box sx={{ py: 1.25 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 0.4, lineHeight: 1 }}>
            {label}
        </Typography>
        {typeof value === 'string' || value == null
            ? <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B' }}>{value || '—'}</Typography>
            : value}
    </Box>
);

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const fmtLocalDate = (d) => d ? d : '—';

const EmployeeInfoPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [employee, setEmployee] = useState(null);
    const [salaryHistory, setSalaryHistory] = useState([]);
    const [tab, setTab] = useState(0);

    const [isEditOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    const [isProvisionOpen, setProvisionOpen] = useState(false);
    const [provisionEmail, setProvisionEmail] = useState('');
    const [provisioning, setProvisioning] = useState(false);

    const [isSalaryOpen, setSalaryOpen] = useState(false);
    const [salaryForm, setSalaryForm] = useState({ amount: '', type: 'MONTHLY', currency: 'CHF', effectiveDate: '', note: '' });
    const [salaryErrors, setSalaryErrors] = useState({});
    const [savingSalary, setSavingSalary] = useState(false);

    const [isDeleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    // Absences
    const { role } = useContext(AuthContext);
    const [absences, setAbsences] = useState([]);
    const [absenceSummary, setAbsenceSummary] = useState(null);
    const [absenceDrawerOpen, setAbsenceDrawerOpen] = useState(false);
    const [savingAbsence, setSavingAbsence] = useState(false);
    const [overlappingNames, setOverlappingNames] = useState([]);
    const [convertTarget, setConvertTarget] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectNote, setRejectNote] = useState('');

    const fetchEmployee = async () => {
        try {
            const data = await EmployeeService.getEmployee(id);
            setEmployee(data);
            setEditForm({
                firstName:              data.firstName || '',
                lastName:               data.lastName || '',
                email:                  data.email || '',
                phone:                  data.phone || '',
                position:               data.position || '',
                department:             data.department || '',
                hireDate:               data.hireDate || '',
                contractType:           data.contractType || '',
                status:                 data.status || 'ACTIVE',
                weeklyHours:            data.weeklyHours || '',
                employmentPercentage:   data.employmentPercentage ?? 100,
                vacationDaysPerYear:    data.vacationDaysPerYear ?? 25,
                workingDays:            data.workingDays || 'MON,TUE,WED,THU,FRI',
                workScheduleType:       data.workScheduleType || 'REDUCED_HOURS',
                emergencyContactName:   data.emergencyContactName || '',
                emergencyContactPhone:  data.emergencyContactPhone || '',
                notes:                  data.notes || '',
            });
            setProvisionEmail(data.email || '');
        } catch {
            showToast('Failed to load employee', 'error');
        }
    };

    const fetchSalary = async () => {
        try {
            const data = await EmployeeService.getSalaryHistory(id);
            setSalaryHistory(data || []);
        } catch {
            showToast('Failed to load salary history', 'error');
        }
    };

    const fetchAbsences = async () => {
        try {
            const [abs, summary] = await Promise.all([
                AbsenceService.getByEmployee(id),
                AbsenceService.getSummary(id, new Date().getFullYear()),
            ]);
            setAbsences(abs || []);
            setAbsenceSummary(summary);
        } catch { /* silently ignore */ }
    };

    useEffect(() => {
        fetchEmployee();
        fetchSalary();
        fetchAbsences();
    }, []); // eslint-disable-line

    const handleAbsenceSave = async (dto) => {
        setSavingAbsence(true);
        try {
            await AbsenceService.create(id, dto);
            await fetchAbsences();
            setAbsenceDrawerOpen(false);
            showToast('Absence request submitted', 'success');
        } catch {
            showToast('Failed to submit request', 'error');
        } finally {
            setSavingAbsence(false);
        }
    };

    const handleAbsenceApprove = async (absence) => {
        try {
            const overlapping = await AbsenceService.overlapCheck(id, absence.startDate, absence.endDate);
            if (overlapping.length > 0) {
                const confirmed = window.confirm(`${overlapping.join(', ')} ${overlapping.length === 1 ? 'is' : 'are'} also absent during this period. Approve anyway?`);
                if (!confirmed) return;
            }
            await AbsenceService.approve(absence.id);
            await fetchAbsences();
            showToast('Absence approved', 'success');
        } catch {
            showToast('Failed to approve absence', 'error');
        }
    };

    const handleAbsenceReject = async (absence, note) => {
        try {
            await AbsenceService.reject(absence.id, note);
            await fetchAbsences();
            setRejectTarget(null);
            showToast('Absence rejected', 'warning');
        } catch {
            showToast('Failed to reject absence', 'error');
        }
    };

    const handleAbsenceDelete = async (absence) => {
        try {
            await AbsenceService.delete(absence.id);
            await fetchAbsences();
            showToast('Absence deleted', 'warning');
        } catch {
            showToast('Failed to delete absence', 'error');
        }
    };

    const handleConvertToSick = async (splitStart, splitEnd, reason) => {
        try {
            await AbsenceService.convertToSick(convertTarget.id, splitStart, splitEnd, reason);
            await fetchAbsences();
            setConvertTarget(null);
            showToast('Converted to sick leave', 'success');
        } catch {
            showToast('Failed to convert absence', 'error');
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...editForm,
                hireDate:    editForm.hireDate || null,
                weeklyHours: editForm.weeklyHours || null,
                contractType: editForm.contractType || null,
            };
            const updated = await EmployeeService.updateEmployee(id, payload);
            setEmployee(updated);
            setEditOpen(false);
            showToast('Employee updated', 'success');
        } catch {
            showToast('Failed to update employee', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleProvision = async () => {
        if (!provisionEmail.trim()) { showToast('Email is required', 'error'); return; }
        setProvisioning(true);
        try {
            const updated = await EmployeeService.provisionAccount(id, provisionEmail);
            setEmployee(updated);
            setProvisionOpen(false);
            showToast('Invite sent', 'success');
        } catch {
            showToast('Failed to send invite', 'error');
        } finally {
            setProvisioning(false);
        }
    };

    const validateSalary = () => {
        const e = {};
        if (!salaryForm.amount || isNaN(salaryForm.amount)) e.amount = 'Valid amount is required';
        if (!salaryForm.effectiveDate) e.effectiveDate = 'Effective date is required';
        setSalaryErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleAddSalary = async () => {
        if (!validateSalary()) return;
        setSavingSalary(true);
        try {
            await EmployeeService.addSalaryRecord(id, {
                amount:        parseFloat(salaryForm.amount),
                type:          salaryForm.type,
                currency:      salaryForm.currency,
                effectiveDate: salaryForm.effectiveDate,
                note:          salaryForm.note || null,
            });
            await fetchSalary();
            setSalaryOpen(false);
            setSalaryForm({ amount: '', type: 'MONTHLY', currency: 'CHF', effectiveDate: '', note: '' });
            showToast('Salary record added', 'success');
        } catch {
            showToast('Failed to add salary record', 'error');
        } finally {
            setSavingSalary(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await EmployeeService.deleteEmployee(id);
            navigate('/employees');
        } catch {
            showToast('Failed to delete employee', 'error');
            setDeleting(false);
        }
    };

    if (!employee) {
        return (
            <Layout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            </Layout>
        );
    }

    const statusColor  = STATUS_COLOR[employee.status]  || '#94A3B8';
    const accountColor = ACCOUNT_COLOR[employee.accountStatus] || '#94A3B8';
    const currentSalary = salaryHistory[0] || null;

    return (
        <Layout>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

                {/* Sticky header */}
                <Box sx={{
                    bgcolor: '#fff', borderBottom: '1px solid #EEF2F6',
                    px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
                    position: 'sticky', top: 0, zIndex: 10,
                }}>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => navigate('/employees')}
                        sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
                    >
                        Employees
                    </Button>

                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />

                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>
                        {employee.firstName} {employee.lastName}
                    </Typography>

                    <Chip
                        label={employee.status?.replace('_', ' ')}
                        size="small"
                        sx={{ bgcolor: statusColor + '18', color: statusColor, fontWeight: 600, fontSize: 11, border: `1px solid ${statusColor}30`, height: 22 }}
                    />

                    <Box sx={{ flex: 1 }} />

                    <Tooltip title="Edit employee">
                        <IconButton size="small" onClick={() => setEditOpen(true)}
                            sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                            <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="More actions">
                        <IconButton size="small" onClick={e => setMenuAnchorEl(e.currentTarget)}
                            sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                            <MoreVertIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>

                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={() => setMenuAnchorEl(null)}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        PaperProps={{ elevation: 0, sx: { borderRadius: 2, minWidth: 200, border: '1px solid #E8EDF2', boxShadow: '0 8px 24px rgba(0,0,0,0.10)', mt: 0.5 } }}
                    >
                        <MenuItem onClick={() => { setEditOpen(true); setMenuAnchorEl(null); }} dense>
                            <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                            Edit employee
                        </MenuItem>
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem onClick={() => { setDeleteOpen(true); setMenuAnchorEl(null); }} dense sx={{ color: '#EF4444' }}>
                            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
                            Delete employee
                        </MenuItem>
                    </Menu>
                </Box>

                {/* Body */}
                <Box sx={{ display: 'flex', gap: 3, p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto', alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>

                    {/* Main column with tabs */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Tabs value={tab} onChange={(_, v) => setTab(v)}
                                sx={{ px: 2, borderBottom: '1px solid #F1F5F9', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44 }, '& .Mui-selected': { color: '#427898' }, '& .MuiTabs-indicator': { bgcolor: '#427898' } }}>
                                <Tab label="Personal Info" />
                                <Tab label="Job Details" />
                                <Tab label="Salary" />
                                <Tab label="Absences" />
                                <Tab label="Notes" />
                            </Tabs>

                            {/* ── Tab 0: Personal Info ── */}
                            {tab === 0 && (
                                <Box sx={{ px: 3, py: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 6 }}>
                                    <FieldRow label="Email"  value={employee.email} />
                                    <FieldRow label="Phone"  value={employee.phone} />
                                    <FieldRow label="Emergency contact" value={employee.emergencyContactName} />
                                    <FieldRow label="Emergency phone"   value={employee.emergencyContactPhone} />
                                </Box>
                            )}

                            {/* ── Tab 1: Job Details ── */}
                            {tab === 1 && (
                                <Box sx={{ px: 3, py: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 6 }}>
                                    <FieldRow label="Position"            value={employee.position} />
                                    <FieldRow label="Department"          value={employee.department} />
                                    <FieldRow label="Contract type"       value={employee.contractType?.replace('_', ' ')} />
                                    <FieldRow label="Employment"          value={employee.employmentPercentage != null ? `${employee.employmentPercentage}%` : null} />
                                    <FieldRow label="Weekly hours"        value={employee.weeklyHours?.toString()} />
                                    <FieldRow label="Vacation days/year"  value={employee.vacationDaysPerYear?.toString()} />
                                    <FieldRow label="Working days"        value={employee.workingDays} />
                                    <FieldRow label="Hire date"           value={fmtLocalDate(employee.hireDate)} />
                                </Box>
                            )}

                            {/* ── Tab 2: Salary ── */}
                            {tab === 2 && (
                                <Box>
                                    <SectionHeader
                                        label="Salary"
                                        action={
                                            <Button size="small" startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                                                onClick={() => setSalaryOpen(true)}
                                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13, color: '#427898', px: 1.5, '&:hover': { bgcolor: '#F0F7FF' } }}>
                                                Record change
                                            </Button>
                                        }
                                    />
                                    {currentSalary && (
                                        <Box sx={{ mx: 3, my: 2, p: 2.5, bgcolor: '#F0F7FF', borderRadius: 2, border: '1px solid #BFDBFE' }}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', textTransform: 'uppercase', letterSpacing: 1, mb: 0.5 }}>
                                                Current salary
                                            </Typography>
                                            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0F172A' }}>
                                                {currentSalary.currency} {parseFloat(currentSalary.amount).toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                                                <Typography component="span" sx={{ fontSize: 13, fontWeight: 400, color: '#64748B', ml: 1 }}>
                                                    / {currentSalary.type?.toLowerCase()}
                                                </Typography>
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.5 }}>
                                                effective {fmtLocalDate(currentSalary.effectiveDate)}
                                            </Typography>
                                        </Box>
                                    )}
                                    {salaryHistory.length === 0 ? (
                                        <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
                                            <Typography sx={{ color: '#CBD5E1', fontSize: 13 }}>No salary records yet</Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ mx: 3, mb: 2 }}>
                                            {salaryHistory.map((rec, i) => (
                                                <Box key={rec.id} sx={{ py: 1.5, borderBottom: i < salaryHistory.length - 1 ? '1px solid #F1F5F9' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>
                                                            {rec.currency} {parseFloat(rec.amount).toLocaleString('de-CH', { minimumFractionDigits: 2 })} / {rec.type?.toLowerCase()}
                                                        </Typography>
                                                        {rec.note && <Typography sx={{ fontSize: 12, color: '#64748B' }}>{rec.note}</Typography>}
                                                    </Box>
                                                    <Typography sx={{ fontSize: 12, color: '#94A3B8', whiteSpace: 'nowrap', ml: 2 }}>
                                                        {fmtLocalDate(rec.effectiveDate)}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* ── Tab 3: Absences ── */}
                            {tab === 3 && (
                                <Box sx={{ p: 3 }}>
                                    <AbsenceBalanceCard summary={absenceSummary} />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setAbsenceDrawerOpen(true)}
                                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13, color: '#427898', border: '1px solid #427898', borderRadius: 1.5, px: 2, '&:hover': { bgcolor: '#F0F7FF' } }}
                                        >
                                            + Request Time Off
                                        </Button>
                                    </Box>
                                    <AbsenceTable
                                        absences={absences}
                                        role={role}
                                        onApprove={handleAbsenceApprove}
                                        onReject={(a) => { setRejectTarget(a); setRejectNote(''); }}
                                        onDelete={handleAbsenceDelete}
                                        onConvertToSick={(a) => setConvertTarget(a)}
                                    />
                                </Box>
                            )}

                            {/* ── Tab 4: Notes ── */}
                            {tab === 4 && (
                                <Box sx={{ px: 3, py: 2 }}>
                                    {employee.notes ? (
                                        <Typography sx={{ fontSize: 13, color: '#1E293B', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                                            {employee.notes}
                                        </Typography>
                                    ) : (
                                        <Typography sx={{ fontSize: 13, color: '#CBD5E1' }}>No notes</Typography>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    {/* Sidebar */}
                    <Box sx={{ width: { md: 272 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, position: { md: 'sticky' }, top: { md: 72 } }}>

                        {/* Summary */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                Summary
                            </Typography>
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>Added</Typography>
                                    <Typography sx={{ fontSize: 13, color: '#1E293B', mt: 0.3 }}>{fmtDate(employee.createdAt)}</Typography>
                                </Box>
                                {currentSalary && (
                                    <Box>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>Current salary</Typography>
                                        <Typography sx={{ fontSize: 13, color: '#1E293B', mt: 0.3 }}>
                                            {currentSalary.currency} {parseFloat(currentSalary.amount).toLocaleString('de-CH', { minimumFractionDigits: 2 })} / {currentSalary.type?.toLowerCase()}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>

                        {/* CRM Account */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                CRM Account
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: employee.accountStatus !== 'ACTIVE' ? 2 : 0 }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: accountColor, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B' }}>
                                    {employee.accountStatus === 'NONE' ? 'No account' : employee.accountStatus === 'INVITED' ? 'Invited' : 'Active'}
                                </Typography>
                            </Stack>
                            {employee.accountStatus !== 'ACTIVE' && (
                                <Button
                                    fullWidth variant="outlined" size="small"
                                    startIcon={<PersonAddIcon sx={{ fontSize: '16px !important' }} />}
                                    onClick={() => { setProvisionEmail(employee.email || ''); setProvisionOpen(true); }}
                                    sx={{ borderColor: '#CBD5E1', color: '#475569', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, '&:hover': { borderColor: '#427898', color: '#427898', bgcolor: '#F0F7FF' } }}
                                >
                                    {employee.accountStatus === 'INVITED' ? 'Re-send invite' : 'Create account'}
                                </Button>
                            )}
                        </Paper>
                    </Box>
                </Box>

                {/* Edit dialog */}
                <Dialog open={isEditOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
                        Edit employee
                        <IconButton size="small" onClick={() => setEditOpen(false)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 0.5 }}>
                            <TextField label="First name" fullWidth value={editForm.firstName || ''}
                                onChange={e => setEditForm(p => ({ ...p, firstName: e.target.value }))} />
                            <TextField label="Last name" fullWidth value={editForm.lastName || ''}
                                onChange={e => setEditForm(p => ({ ...p, lastName: e.target.value }))} />
                            <TextField label="Email" fullWidth value={editForm.email || ''}
                                onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} />
                            <TextField label="Phone" fullWidth value={editForm.phone || ''}
                                onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
                            <TextField label="Position" fullWidth value={editForm.position || ''}
                                onChange={e => setEditForm(p => ({ ...p, position: e.target.value }))} />
                            <TextField label="Department" fullWidth value={editForm.department || ''}
                                onChange={e => setEditForm(p => ({ ...p, department: e.target.value }))} />
                            <TextField label="Hire date" fullWidth type="date" InputLabelProps={{ shrink: true }}
                                value={editForm.hireDate || ''} onChange={e => setEditForm(p => ({ ...p, hireDate: e.target.value }))} />
                            <TextField label="Contract type" fullWidth select value={editForm.contractType || ''}
                                onChange={e => setEditForm(p => ({ ...p, contractType: e.target.value }))}>
                                <MenuItem value="">—</MenuItem>
                                {CONTRACT_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
                            </TextField>
                            <TextField label="Status" fullWidth select value={editForm.status || 'ACTIVE'}
                                onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                            </TextField>
                            <TextField label="Weekly hours" fullWidth type="number" value={editForm.weeklyHours || ''}
                                onChange={e => setEditForm(p => ({ ...p, weeklyHours: e.target.value }))} />
                            <TextField label="Employment %" fullWidth type="number" inputProps={{ min: 10, max: 100, step: 10 }}
                                value={editForm.employmentPercentage || ''}
                                onChange={e => setEditForm(p => ({ ...p, employmentPercentage: e.target.value ? parseInt(e.target.value) : null }))} />
                            <TextField label="Vacation days / year" fullWidth type="number" inputProps={{ min: 0 }}
                                value={editForm.vacationDaysPerYear || ''}
                                onChange={e => setEditForm(p => ({ ...p, vacationDaysPerYear: e.target.value ? parseInt(e.target.value) : null }))} />
                            <TextField label="Emergency contact" fullWidth value={editForm.emergencyContactName || ''}
                                onChange={e => setEditForm(p => ({ ...p, emergencyContactName: e.target.value }))} />
                            <TextField label="Emergency phone" fullWidth value={editForm.emergencyContactPhone || ''}
                                onChange={e => setEditForm(p => ({ ...p, emergencyContactPhone: e.target.value }))} />
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <TextField label="Notes" fullWidth multiline rows={3} value={editForm.notes || ''}
                                    onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}>
                            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Provision account dialog */}
                <Dialog open={isProvisionOpen} onClose={() => !provisioning && setProvisionOpen(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
                    <Box sx={{ height: 4, bgcolor: '#427898' }} />
                    <DialogTitle sx={{ fontWeight: 700, color: '#1a2332', pb: 1 }}>
                        {employee.accountStatus === 'INVITED' ? 'Re-send invite' : 'Create CRM account'}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                            An invite email will be sent so the employee can set their password and log in.
                        </Typography>
                        <TextField
                            fullWidth label="Email address" type="email" autoFocus
                            value={provisionEmail} onChange={e => setProvisionEmail(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={() => setProvisionOpen(false)} disabled={provisioning} sx={{ textTransform: 'none', color: '#64748b' }}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleProvision} disabled={provisioning}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}>
                            {provisioning ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send invite'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Add salary record dialog */}
                <Dialog open={isSalaryOpen} onClose={() => setSalaryOpen(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
                        Record salary change
                        <IconButton size="small" onClick={() => setSalaryOpen(false)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
                                <TextField label="Amount" type="number" fullWidth value={salaryForm.amount}
                                    onChange={e => setSalaryForm(p => ({ ...p, amount: e.target.value }))}
                                    error={!!salaryErrors.amount} helperText={salaryErrors.amount} />
                                <TextField label="Currency" select fullWidth value={salaryForm.currency}
                                    onChange={e => setSalaryForm(p => ({ ...p, currency: e.target.value }))}>
                                    {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </TextField>
                            </Box>
                            <TextField label="Type" select fullWidth value={salaryForm.type}
                                onChange={e => setSalaryForm(p => ({ ...p, type: e.target.value }))}>
                                {SALARY_TYPES.map(t => <MenuItem key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</MenuItem>)}
                            </TextField>
                            <TextField label="Effective date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                                value={salaryForm.effectiveDate}
                                onChange={e => setSalaryForm(p => ({ ...p, effectiveDate: e.target.value }))}
                                error={!!salaryErrors.effectiveDate} helperText={salaryErrors.effectiveDate} />
                            <TextField label="Note (optional)" fullWidth value={salaryForm.note}
                                onChange={e => setSalaryForm(p => ({ ...p, note: e.target.value }))} />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={() => setSalaryOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button variant="contained" onClick={handleAddSalary} disabled={savingSalary}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}>
                            {savingSalary ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete confirm */}
                <ConfirmModal
                    open={isDeleteOpen}
                    onClose={() => !deleting && setDeleteOpen(false)}
                    onConfirm={handleDelete}
                    question={`Delete employee "${employee.firstName} ${employee.lastName}"? This cannot be undone.`}
                    isLoading={deleting}
                />

                {/* Absence request drawer */}
                <AbsenceRequestDrawer
                    open={absenceDrawerOpen}
                    onClose={() => setAbsenceDrawerOpen(false)}
                    onSave={handleAbsenceSave}
                    overlappingNames={overlappingNames}
                    saving={savingAbsence}
                />

                {/* Convert to sick dialog */}
                <ConvertToSickDialog
                    open={!!convertTarget}
                    absence={convertTarget}
                    onClose={() => setConvertTarget(null)}
                    onConfirm={handleConvertToSick}
                />

                {/* Reject dialog */}
                {rejectTarget && (
                    <Dialog open onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #EEF2F6' }}>Reject Absence</DialogTitle>
                        <DialogContent sx={{ pt: 2.5 }}>
                            <TextField
                                label="Reason for rejection (optional)"
                                fullWidth size="small" multiline rows={3}
                                value={rejectNote}
                                onChange={e => setRejectNote(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid #EEF2F6', gap: 1 }}>
                            <Button onClick={() => setRejectTarget(null)} sx={{ color: '#64748B', textTransform: 'none' }}>Cancel</Button>
                            <Button
                                variant="contained" color="error"
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none' }}
                                onClick={() => handleAbsenceReject(rejectTarget, rejectNote)}
                            >
                                Reject
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Box>
        </Layout>
    );
};

export default EmployeeInfoPage;
